import { useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";

import Tabs from "../constants/tabs";
import {
  fetchImagesFromUid,
  fetchUidFromVanity,
  fetchUserData,
} from "../firebase/user";
import { setSelectedSidebarUser } from "../redux/createSidebarSlice";
import { setTab } from "../redux/createTabSlice";
import Loader from "./Loader";
import DesktopView from "./public-profile/DesktopView";
import MobileView from "./public-profile/MobileView";

interface VanityProfileProps {
  vanity: string;
}

export const VanityProfile = ({ vanity }: VanityProfileProps) => {
  const dispatch = useDispatch();
  const isMdSize = useMediaQuery("(min-width:900px)");
  const router = useRouter();

  const {
    isLoading: dataIsLoading,
    error: dataError,
    data,
  } = useQuery(
    ["vanityProfile", vanity],
    async () => {
      if (!vanity || typeof vanity !== "string") {
        return { imageData: null, userData: null };
      }

      if (vanity === "browse") {
        return { imageData: null, userData: null };
      }

      try {
        const uid = await fetchUidFromVanity(vanity);

        if (!uid) {
          // Redirect to 404 page or home page if vanity URL not found
          router.replace("/404");
          return { imageData: null, userData: null };
        }

        const [userData, imageData] = await Promise.all([
          fetchUserData(uid),
          fetchImagesFromUid(uid),
        ]);

        return {
          imageData: imageData || null,
          userData: userData || null,
        };
      } catch (error) {
        console.error("Error fetching vanity profile:", error);
        router.replace("/404");
        return { imageData: null, userData: null };
      }
    },
    {
      enabled: !!vanity && typeof vanity === "string",
      retry: 1,
      retryDelay: 1000,
    }
  );

  const userData = data?.userData;
  const imageData = data?.imageData;

  useEffect(() => {
    if (userData) {
      dispatch(setSelectedSidebarUser(userData));
      dispatch(setTab(Tabs.Search));
    }

    return () => {
      dispatch(setSelectedSidebarUser(null));
    };
  }, [userData, dispatch]);

  // Show loader while data is loading
  if (dataIsLoading || !vanity) {
    return <Loader />;
  }

  // Show error state
  if (dataError) {
    console.error("Error loading profile:", dataError);
    router.replace("/404");
    return <Loader />;
  }

  // Show loader if no user data
  if (!userData || !imageData) {
    return <Loader />;
  }

  const userCreatedAt = userData?.createdAt && new Date(userData.createdAt);

  return isMdSize ? (
    <DesktopView userData={userData} imageData={imageData} />
  ) : (
    <MobileView userData={userData} imageData={imageData} />
  );
};
